import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { FlaskConical, Users, Mic2, Sparkles, AlertTriangle, Loader2, LogOut, RotateCw } from 'lucide-react';
import { Button } from '../ui/button';

const PERSONAS = [
  { key: 'user', label: 'Buyer', icon: Users, route: '/dashboard', description: 'See the site as a buyer — Browse Artists, cart, dashboard, orders.' },
  { key: 'artist', label: 'Artist', icon: Mic2, route: '/artist-dashboard', description: 'See the Artist Dashboard — orders, earnings, withdrawals, profile.' },
  { key: 'agent', label: 'Agent', icon: Sparkles, route: '/agent-dashboard', description: 'See the Agent Dashboard — Weekly Lineup, agent orders, earnings.' },
];

export function TestModeTab() {
  const { testMode } = useAuth();
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.getTestMode()
      .then(res => setAvailability(res.data.availability || {}))
      .catch(() => toast.error('Failed to load test-mode info'))
      .finally(() => setLoading(false));
  }, []);

  const enter = async (persona) => {
    setBusy(persona.key);
    try {
      const res = await api.enterTestMode(persona.key);
      toast.success(`Now viewing as ${persona.label} — ${res.data.persona.name}`, {
        description: 'You\'re inside a private sandbox. Edits live only here.',
      });
      // Hard navigate so route guards re-read auth from scratch.
      window.location.assign(persona.route);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enter test mode');
      setBusy(null);
    }
  };

  const reset = async () => {
    if (!window.confirm('Discard all changes you made in this test session and re-sync with live data?')) return;
    setBusy('reset');
    try {
      await api.resetTestMode();
      toast.success('Sandbox reset — you now have a fresh copy of production data.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset sandbox');
    } finally {
      setBusy(null);
    }
  };

  const exit = async () => {
    setBusy('exit');
    try {
      await api.exitTestMode();
      toast.success('Exited test mode — sandbox deleted.');
      window.location.assign('/admin');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to exit test mode');
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div>;
  }

  const active = testMode?.active;

  return (
    <div className="space-y-6">
      <div className="bg-surface border border-[#262626] rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <FlaskConical className="w-5 h-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold">Testing Mode</h2>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Step into a private, isolated copy of Tune4Tune to see how the site looks and behaves as a
              Buyer, Artist, or Agent. <strong className="text-white">Every edit you make stays in your
              sandbox</strong> — change prices, place orders, accept dubplates — none of it touches live data.
              Sandbox is wiped clean when you exit.
            </p>
          </div>
        </div>
      </div>

      {active && (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="test-mode-active-card">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-300">Test mode is ACTIVE</p>
              <p className="text-xs text-[#A1A1AA]">Currently viewing as {testMode.persona_role}: {testMode.persona_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={reset} disabled={busy === 'reset'} variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="settings-reset-test-mode">
              {busy === 'reset' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />}
              Reset Sandbox
            </Button>
            <Button onClick={exit} disabled={busy === 'exit'} className="bg-black text-amber-300 hover:bg-black/80" data-testid="settings-exit-test-mode">
              {busy === 'exit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogOut className="w-4 h-4 mr-2" />}
              Exit Test Mode
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PERSONAS.map((persona) => {
          const Icon = persona.icon;
          const available = availability[persona.key];
          const current = active && testMode.persona_role === persona.key;
          return (
            <div
              key={persona.key}
              className={`bg-surface border ${current ? 'border-lime' : 'border-[#262626]'} rounded-xl p-5 flex flex-col`}
              data-testid={`test-persona-card-${persona.key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-5 h-5 ${current ? 'text-lime' : 'text-[#A1A1AA]'}`} />
                <h3 className="font-heading font-bold">{persona.label}</h3>
                {current && <span className="text-xs text-lime font-semibold ml-auto">Active</span>}
              </div>
              <p className="text-xs text-[#A1A1AA] flex-1 mb-4">{persona.description}</p>
              <Button
                onClick={() => enter(persona)}
                disabled={!available || busy === persona.key || current}
                className={current ? 'btn-secondary' : 'btn-primary'}
                data-testid={`enter-test-mode-${persona.key}`}
              >
                {busy === persona.key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {current ? 'Currently Viewing' : (!available ? 'No account available' : `View as ${persona.label}`)}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
